import { useQuery } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import type { CheckInsHistoryInstance, CheckInInstance, CheckInUpdatePayload } from './types';

const getCurrentCheckIn = async () => {
  try {
    const url = endpoints.checkIns.currentCheckIn;
    const response = await sendRequest({ url, method: 'GET', credentials: 'include' });
    return response.entity;
  } catch (e) {
    console.error('Error failed to get current check in', e);
    throw e;
  }
};

export const useGetCurrentCheckIns = () =>
  useQuery<CheckInInstance>({ queryKey: [REACT_QUERY_KEYS.checkIns.currentCheckIn], queryFn: getCurrentCheckIn });

const getPastCheckInsHistory = async () => {
  try {
    const url = endpoints.checkIns.checkInsHistory;
    const response = await sendRequest({ url, method: 'GET', credentials: 'include' });
    return response.entity;
  } catch (e) {
    console.error('Error failed to get past check ins history', e);
    throw e;
  }
};

export const useGetPastCheckInsHistory = () =>
  useQuery<CheckInsHistoryInstance[]>({
    queryKey: [REACT_QUERY_KEYS.checkIns.pastCheckInsHistory],
    queryFn: getPastCheckInsHistory,
  });

const getCheckInByInstanceId = async (id: number) => {
  try {
    const url = endpoints.checkIns.checkInByInstanceId(id);
    const response = await sendRequest({ url, method: 'GET', credentials: 'include' });
    return response.entity;
  } catch (e) {
    console.error('Error failed to get check in by instance id', e);
    throw e;
  }
};

export const useGetCheckInByInstanceId = (id: number) =>
  useQuery<CheckInInstance>({
    queryKey: [REACT_QUERY_KEYS.checkIns.checkInInstance, id],
    queryFn: () => getCheckInByInstanceId(id),
    enabled: !!id,
  });

export const updateCheckIn = async (updatedCheckIn: CheckInUpdatePayload) => {
  try {
    const url = endpoints.checkIns.updateCheckIn;
    const response = await sendRequest({ url, method: 'PUT', credentials: 'include', body: updatedCheckIn });
    return response.entity;
  } catch (e) {
    console.error('Error failed to update check in', e);
    throw e;
  }
};

export const submitCheckIn = async (instanceId: number) => {
  try {
    const url = endpoints.checkIns.completeCheckIn(instanceId);
    const response = await sendRequest({ url, method: 'POST', credentials: 'include' });
    return response.entity;
  } catch (e) {
    console.error('Error failed to submit check in', e);
    throw e;
  }
};
